<template>
  <div class="one-galleria-wrapper" :class="{ 'p-invalid': hasError }">
    <Galleria
      v-model:activeIndex="activeIndex"
      :value="images"
      :numVisible="numVisible"
      :showThumbnails="showThumbnails"
      :showIndicators="showIndicators"
      :showIndicatorsOnItem="showIndicatorsOnItem"
      :changeItemOnIndicatorHover="changeItemOnIndicatorHover"
      :circular="circular"
      :autoPlay="autoPlay"
      :transitionInterval="transitionInterval"
      :showItemNavigators="showItemNavigators"
      :showThumbnailNavigators="showThumbnailNavigators"
      :showItemNavigatorsOnHover="showItemNavigatorsOnHover"
      :thumbnailsPosition="thumbnailsPosition"
      :verticalThumbnailViewPortHeight="verticalThumbnailViewPortHeight"
      :responsiveOptions="responsiveOptions"
      :thumbnailHeight="thumbnailHeight"
      :thumbnailWidth="thumbnailWidth"
      :containerStyle="containerStyle"
      :containerClass="containerClass"
      v-bind="$attrs"
      @item-change="onItemChange"
    >
      <!-- Item Template -->
      <template #item="slotProps">
        <div class="one-galleria-item-wrapper">
          <img
            :src="slotProps.item.src"
            :alt="slotProps.item.alt"
            class="one-galleria-item"
          />
          <div v-if="slotProps.item.title" class="one-galleria-item-title">
            {{ slotProps.item.title }}
          </div>
        </div>
      </template>

      <!-- Thumbnail Template -->
      <template #thumbnail="slotProps">
        <img
          :src="slotProps.item.thumbnail || slotProps.item.src"
          :alt="slotProps.item.alt"
          class="one-galleria-thumbnail"
        />
      </template>

      <!-- Optional Slots -->
      <template v-if="$slots.indicator" #indicator="slotProps">
        <slot name="indicator" v-bind="slotProps" />
      </template>

      <template v-if="$slots.caption" #caption="slotProps">
        <slot name="caption" v-bind="slotProps" />
      </template>

      <template v-if="$slots.previousItemIcon" #previousitemicon>
        <slot name="previousItemIcon" />
      </template>

      <template v-if="$slots.nextItemIcon" #nextitemicon>
        <slot name="nextItemIcon" />
      </template>

      <template v-if="$slots.previousThumbnailIcon" #previousthumbnailicon>
        <slot name="previousThumbnailIcon" />
      </template>

      <template v-if="$slots.nextThumbnailIcon" #nextthumbnailicon>
        <slot name="nextThumbnailIcon" />
      </template>
    </Galleria>
    <small v-if="hasError" class="p-error">{{ fieldError }}</small>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import Galleria from 'primevue/galleria'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import type { GalleriaProps, GalleriaEmits, GalleriaResponsiveOptions } from './types'

const props = withDefaults(defineProps<GalleriaProps>(), {
  modelValue: 0,
  showThumbnails: true,
  showIndicators: false,
  showIndicatorsOnItem: true,
  showItemNavigators: true,
  showThumbnailNavigators: true,
  changeItemOnIndicatorHover: false,
  circular: false,
  autoPlay: false,
  transitionInterval: 4000,
  showItemNavigatorsOnHover: true,
  thumbnailsPosition: 'bottom',
  numVisible: 5,
  thumbnailHeight: '50px',
  thumbnailWidth: '70px',
  containerClass: 'custom-galleria',
  responsiveOptions: () => [
    {
      breakpoint: '1024px',
      numVisible: 5
    },
    {
      breakpoint: '768px',
      numVisible: 3
    },
    {
      breakpoint: '560px',
      numVisible: 1
    }
  ]
})

const emit = defineEmits<GalleriaEmits>()

const { t } = useLocalization()
const activeIndex = ref(props.modelValue)
const { formState } = useFormContext()

// Form integration
const hasError = computed(() => {
  if (!formState || !props.name) return false
  return props.name in formState.errors.value
})

const fieldError = computed(() => {
  if (!formState || !props.name) return ''
  const errors = formState.errors.value[props.name]
  return errors ? errors[0] : ''
})

// Watch for value changes
watch(activeIndex, (newValue) => {
  if (formState && props.name) {
    formState.setFieldValue(props.name, newValue)
  }
  emit('update:modelValue', newValue)
})

// Watch for external value changes
watch(
  () => props.modelValue,
  (newValue) => {
    activeIndex.value = newValue
  }
)

const onItemChange = (event: { index: number }) => {
  emit('item-change', event)
}
</script>

<style lang="scss">
.one-galleria-wrapper {
  .one-galleria-item-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .one-galleria-item {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
  }

  .one-galleria-item-title {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    padding: 0.5rem;
    text-align: center;
  }

  .one-galleria-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  .p-galleria {
    &.p-galleria-indicators {
      .p-galleria-indicator {
        button {
          background-color: var(--surface-d);
          width: 1rem;
          height: 1rem;
          transition: background-color 0.2s;
          border-radius: 50%;
          
          &:hover {
            background-color: var(--primary-color);
          }

          &.p-highlight {
            background-color: var(--primary-color);
          }
        }
      }
    }

    .p-galleria-thumbnail-container {
      background: var(--surface-b);
      padding: 1rem 0;
    }

    .p-galleria-thumbnail-item-content {
      &:focus {
        box-shadow: 0 0 0 0.2rem var(--primary-color);
      }
    }
  }

  &.p-invalid {
    .p-galleria {
      border-color: var(--red-500);
    }
  }
}
</style>
